import { ref } from "vue";
import { RouteRecordRaw, Router, createRouter, createWebHistory } from "vue-router";
import myTaskRouter from "./modules/myTask";
import dataAnalysisRouter from "./modules/dataAnalysis";
import priceMonitorRouter from "./modules/priceMonitor";
import aiAssistantRouter from "./modules/aiAssistant";
import staticRouter from "@/routers/modules/static";
import loginRouter from "@/routers/modules/login";
import meRouters from "@/routers/modules/me";
import { LOGIN_URL, NO_PERMISSION_URL, ROUTER_WHITE_LIST } from "@/config";
import userMessage from "@/routers/modules/userMessage";
import { purchaseSalesInventoryRouters } from "@/routers/purchaseSalesInventory";
import { warehouseSupervisionRouters } from "@/routers/warehouseSupervision";
import { warehouseManagementRouters } from "@/routers/warehouseManagement";
import { useTabsStore } from "@/stores/tabs";
import { useUserStore } from "@/stores/user";
import { getUserInfo } from "@/utils/common";
import aiRiskControlRouter from "@/routers/modules/aiRiskControl";
import { tradeManagementRouters } from "@/routers/tradeManagement";
// 权限路由
export const syncRouters = [
    ...myTaskRouter,
    ...purchaseSalesInventoryRouters,
    ...warehouseSupervisionRouters,
    ...tradeManagementRouters,
    ...warehouseManagementRouters,
    ...dataAnalysisRouter,
    ...priceMonitorRouter,
    ...aiAssistantRouter,
    ...aiRiskControlRouter,
    ...meRouters,
    ...userMessage
];
/**
 * @description 📚 路由参数配置简介
 * @param path ==> 路由菜单访问路径
 * @param name ==> 路由 name (对应页面组件 name, 可用作 KeepAlive 缓存标识 && 按钮权限筛选)
 * @param redirect ==> 路由重定向地址
 * @param component ==> 视图文件路径
 * @param meta ==> 路由菜单元信息
 * @param meta.icon ==> 菜单和面包屑对应的图标
 * @param meta.title ==> 路由标题 (用作 document.title || 菜单的名称)
 * @param meta.activeMenu ==> 当前路由为详情页时，需要高亮的菜单
 * @param meta.hidden ==> 是否在菜单中隐藏 (通常列表详情页需要隐藏), boolean | () => boolean
 * @param meta.keepAlive ==> 当前路由是否缓存, boolean
 * @param meta.hideMenu ==> 当前路由是否显示左侧菜单
 * @param meta.hideTabs ==> 当前路由是否显示顶部页签，true时隐藏页签
 * */
const allRouters = [...staticRouter, ...loginRouter];
const router: Router = createRouter({
    history: createWebHistory(import.meta.env.VITE_PUBLIC_PATH),
    routes: allRouters
});

// 首次加载页面刷新UserInfo
const flushedUserInfo = ref(false);

// 存储已添加的路由名称 isSAAS
const addedRoutes = new Set<number>();
// 根据角色动态加入路由
export const addRoleRoutes = async (role: number) => {
    if (addedRoutes.has(role)) return;
    try {
        const userStore = useUserStore();
        const processRoute = (route: RouteRecordRaw) => {
            // 处理 meta.title
            if (route.meta && typeof route.meta.title === "function") {
                route.meta.title = route.meta.title({ userStore });
            }
            // 递归处理子路由
            if (route.children && Array.isArray(route.children)) {
                route.children.forEach(childRoute => processRoute(childRoute));
            }
            return route;
        };
        const roleRoutes = syncRouters.map(route => processRoute({ ...route }));
        roleRoutes.forEach(route => {
            if (!router.hasRoute(route.name as string)) {
                router.addRoute(route);
            }
        });
        addedRoutes.add(role);
    } catch (error) {
        console.error("Failed to add role routes:", error);
    }
};
const CURRENT_ENV = import.meta.env?.VITE_USER_ENV || "";
const SASS_LOGIN_URL = import.meta.env?.VITE_LOGIN_APP_URL || "";
/**
 * @description 路由拦截 beforeEach
 * */
router.beforeEach(async (to, from, next) => {
    try {
        const userStore = useUserStore();
        const tabStore = useTabsStore();
        //判断路径中是否有token
        if (to?.query?.token) {
            userStore.setToken(to.query.token as string);
            await getUserInfo();
            if (!userStore.userInfo?.proxyAccount?.roles?.length && to.name === "MyTaskIndex") {
                return next({ path: "/me/info", replace: true });
            } else {
                return next({ path: "/", replace: true });
            }
        }
        //判断saas无token-或者saas跳login页进行跳转
        const isLoginPath = to.path.toLowerCase().includes(LOGIN_URL);
        if (CURRENT_ENV === "saas" && SASS_LOGIN_URL && (!userStore.token || isLoginPath)) {
            window.location.replace(SASS_LOGIN_URL);
        }
        tabStore.connectWebsocket();
        // 设置标题
        const title = userStore?.theme?.systemName || import.meta.env.VITE_GLOB_APP_TITLE;
        if (title) {
            document.title = (
                (to.meta?.title as string) ? `${(to.meta?.title as string).trim() ?? ""} - ${title}` : title
            ) as string;
        }

        // 访问登录页则停留登录页
        if (to.path.toLocaleLowerCase().includes(LOGIN_URL)) {
            return next();
        }
        // 判断访问页面是否在路由白名单地址(静态路由)中，如果存在直接放行
        if (ROUTER_WHITE_LIST.includes(to.path)) return next();

        // 跳转前判断是否有 Token，没有重定向到 login 页面
        if (!userStore.token && to.name !== "Authentication") {
            return next({ path: LOGIN_URL, replace: true });
        }

        if (!flushedUserInfo.value) {
            await getUserInfo();
            flushedUserInfo.value = true;
        }
        if (!addedRoutes.has(userStore.userInfo.systemType)) {
            await addRoleRoutes(userStore.userInfo.systemType);
            // 判断是否组织机构认证了，并且去的页面是否是待办
            if (!userStore?.userInfo?.proxyAccount?.applyState && to.fullPath === "/myTask/index") {
                return next({ path: "/me/info", replace: true });
            } else {
                return next(to.fullPath);
            }
        }
        // 判断是否组织机构认证了，并且去的页面是否是待办
        if (!userStore?.userInfo?.proxyAccount?.applyState && to.fullPath === "/myTask/index") {
            return next({ path: "/me/info", replace: true });
        }
        // 6.如果没有菜单列表，就重新请求菜单列表并添加动态路由
        if (to.meta.authOptions !== undefined && (to.meta.authOptions as { label: string; value: string }[]).length) {
            const permissions = userStore.userInfo.permissions || [];
            const list = (to.meta.authOptions as { label: string; value: string }[]).map(elem =>
                permissions.includes(Number(elem.value))
            );
            if (list.includes(true)) {
                return next();
            } else {
                return next({ path: NO_PERMISSION_URL });
            }
        }
    } catch (error) {
        console.log(error);
    }
    next();
});

export default router;
